import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function NavigatePage(): Node {
  return html`
    <div>
      <h1>Programmatic Navigation</h1>
      <p>Use <code>navigate(path)</code> to change routes from code — after form submissions, authentication, or any event handler.</p>

      <h2>Basic Usage</h2>
      ${CodeBlock(`import { navigate } from 'onefold';

// Navigate to a path
navigate('/dashboard');

// Navigate with a dynamic segment
const userId = '42';
navigate(\`/users/\${userId}\`);`)}

      <h2>Common Patterns</h2>

      <h3>After Form Submit</h3>
      ${CodeBlock(`function LoginForm(): Node {
  const handleSubmit = async () => {
    const success = await login(email(), password());
    if (success) {
      navigate('/dashboard');
    }
  };

  return html\`
    <form onsubmit=\${(e: Event) => { e.preventDefault(); handleSubmit(); }}>
      <!-- form fields -->
      <button type="submit">Log In</button>
    </form>
  \`;
}`)}

      <h3>Conditional Redirect</h3>
      ${CodeBlock(`import { navigate, createEffect } from 'onefold';

createEffect(() => {
  if (!isAuthenticated()) {
    navigate('/login');
  }
});`)}

      ${Callout('navigate() uses the History API (pushState) under the hood. The browser URL updates without a page reload.')}

      <h2>API</h2>
      <table>
        <tr><th>Function</th><th>Parameters</th><th>Description</th></tr>
        <tr><td><code>navigate</code></td><td><code>path: string</code></td><td>Push a new entry to browser history and trigger route matching.</td></tr>
      </table>

      <h2>Try It</h2>
      <p>Click the buttons to navigate programmatically:</p>

      ${Playground(`import { html, mount, Router, navigate, currentRoute, configureRouter } from 'onefold';

configureRouter({ hash: true });

function Home() {
  return html\`
    <div style="padding:16px">
      <h2>Dashboard</h2>
      <p>You are on the dashboard.</p>
      <div style="display:flex;gap:8px;margin-top:12px">
        <button onclick=\${() => navigate('/profile')}>Go to Profile</button>
        <button onclick=\${() => navigate('/settings')}>Go to Settings</button>
      </div>
    </div>
  \`;
}

function Profile() {
  return html\`
    <div style="padding:16px">
      <h2>Profile</h2>
      <p>User profile page.</p>
      <button onclick=\${() => navigate('/')}>Back to Dashboard</button>
    </div>
  \`;
}

function Settings() {
  return html\`
    <div style="padding:16px">
      <h2>Settings</h2>
      <p>App settings page.</p>
      <button onclick=\${() => navigate('/')}>Back to Dashboard</button>
    </div>
  \`;
}

function App() {
  return html\`
    <div>
      <div style="border:1px solid #e5e7eb;border-radius:8px;min-height:140px">
        \${Router([
          { path: '/', view: () => Home() },
          { path: '/profile', view: () => Profile() },
          { path: '/settings', view: () => Settings() },
        ], () => html\`<p style="padding:16px">Not found</p>\`)}
      </div>
      <p style="margin-top:8px;font-size:12px;color:#666">Route: \${() => currentRoute()}</p>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'Programmatic Navigation with navigate()')}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/routing/link">Link</a> — declarative navigation with active state</li>
        <li><a href="/routing/router">Router</a> — client-side routing overview</li>
      </ul>
    </div>
  `;
}
